import React, { useState } from 'react';
import { 
  FiCheck, 
  FiX, 
  FiTrash2, 
  FiEdit3, 
  FiUserCheck, 
  FiUserX,
  FiSettings,
  FiChevronDown
} from 'react-icons/fi';

export default function BulkActionsBar({ 
  selectedCount = 0,
  entityType = 'items',
  availableActions = [],
  onAction,
  onClearSelection
}) {
  const [showActionMenu, setShowActionMenu] = useState(false);

  if (selectedCount === 0) return null;

  // Default action sets for different entity types
  const defaultActions = {
    users: [
      { id: 'activate', label: 'Activate', icon: FiCheck, color: '#10B981', confirmRequired: true },
      { id: 'suspend', label: 'Suspend', icon: FiX, color: '#EF4444', confirmRequired: true },
      { id: 'change_role', label: 'Change Role', icon: FiUserCheck, color: '#3B82F6', hasSubmenu: true },
      { id: 'delete', label: 'Delete', icon: FiTrash2, color: '#EF4444', confirmRequired: true, dangerous: true }
    ],
    organizations: [
      { id: 'activate', label: 'Activate', icon: FiCheck, color: '#10B981', confirmRequired: true },
      { id: 'deactivate', label: 'Deactivate', icon: FiX, color: '#EF4444', confirmRequired: true },
      { id: 'settings', label: 'Update Settings', icon: FiSettings, color: '#8B5CF6', hasSubmenu: true }
    ],
    questions: [
      { id: 'approve', label: 'Approve', icon: FiCheck, color: '#10B981', confirmRequired: true },
      { id: 'reject', label: 'Reject', icon: FiX, color: '#EF4444', confirmRequired: true },
      { id: 'delete', label: 'Delete', icon: FiTrash2, color: '#EF4444', confirmRequired: true, dangerous: true }
    ],
    reports: [
      { id: 'resolve', label: 'Mark Resolved', icon: FiCheck, color: '#10B981', confirmRequired: true },
      { id: 'dismiss', label: 'Dismiss', icon: FiX, color: '#F59E0B', confirmRequired: true },
      { id: 'escalate', label: 'Escalate', icon: FiSettings, color: '#EF4444', confirmRequired: true }
    ]
  };

  const actions = availableActions.length > 0 ? availableActions : (defaultActions[entityType] || []);

  const handleAction = async (actionId) => {
    const action = actions.find(a => a.id === actionId);
    
    if (action?.confirmRequired) {
      const message = action.dangerous 
        ? `Are you sure you want to ${action.label.toLowerCase()} ${selectedCount} ${entityType}? This action cannot be undone.`
        : `Are you sure you want to ${action.label.toLowerCase()} ${selectedCount} ${entityType}?`;
        
      if (!window.confirm(message)) {
        return;
      }
    }

    try {
      await onAction(actionId);
      setShowActionMenu(false);
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '2rem',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1000,
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
      border: '1px solid #E2E8F0',
      padding: '1rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      minWidth: '400px',
      animation: 'slideUp 0.3s ease-out'
    }}>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>

      {/* Selection Info */}
      <div style={{ flex: 1 }}>
        <span style={{ 
          fontSize: '0.875rem', 
          fontWeight: '500', 
          color: '#1F2937' 
        }}>
          {selectedCount} {entityType} selected
        </span>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {/* Primary actions (first 3) */}
        {actions.slice(0, 3).map((action) => (
          <button
            key={action.id}
            onClick={() => handleAction(action.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: action.color,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <action.icon size={14} />
            {action.label}
          </button>
        ))}

        {/* More actions menu */}
        {actions.length > 3 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowActionMenu(!showActionMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#6B7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              More
              <FiChevronDown size={12} />
            </button>

            {showActionMenu && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                right: 0,
                marginBottom: '0.5rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '1px solid #E2E8F0',
                padding: '0.5rem 0',
                minWidth: '180px',
                zIndex: 1001
              }}>
                {actions.slice(3).map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleAction(action.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      width: '100%',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'transparent',
                      color: action.dangerous ? '#EF4444' : '#374151',
                      border: 'none',
                      fontSize: '0.875rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = action.dangerous ? '#FEF2F2' : '#F9FAFB';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <action.icon size={14} style={{ color: action.color }} />
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Clear Selection */}
        <button
          onClick={onClearSelection}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.5rem',
            backgroundColor: 'transparent',
            color: '#6B7280',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            marginLeft: '0.5rem'
          }}
          title="Clear selection"
        >
          <FiX size={16} />
        </button>
      </div>
    </div>
  );
}